import React, { ReactElement, ReactNode, cloneElement, isValidElement } from 'react';
import './FormField.css';

export interface FormControlProps {
  id: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  className: string;
}

export interface FormFieldProps {
  id: string;
  name?: string;
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  /** A native input, select or textarea, or a render prop receiving wired props. */
  children: ReactElement | ((control: FormControlProps) => ReactNode);
}

/**
 * Label above a native control with hint and error wired through
 * aria-describedby. It never reorders or replaces the control.
 */
const FormField: React.FC<FormFieldProps> = ({ id, name, label, hint, error, required = false, disabled = false, className, children }) => {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  const control: FormControlProps = {
    id,
    name,
    required: required || undefined,
    disabled: disabled || undefined,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
    className: 'luma-field__control',
  };

  let rendered: ReactNode;
  if (typeof children === 'function') {
    rendered = children(control);
  } else if (isValidElement(children)) {
    const existing = (children.props as { className?: string; 'aria-describedby'?: string }) || {};
    const mergedDescribedBy = [describedBy, existing['aria-describedby']].filter(Boolean).join(' ') || undefined;
    rendered = cloneElement(children as ReactElement<Record<string, unknown>>, {
      ...control,
      'aria-describedby': mergedDescribedBy,
      className: [control.className, existing.className].filter(Boolean).join(' '),
    });
  } else {
    rendered = children;
  }

  return (
    <div className={`luma-field${error ? ' luma-field--invalid' : ''}${className ? ` ${className}` : ''}`}>
      <label className="luma-field__label" htmlFor={id}>
        {label}
        {required && <span className="luma-field__required"> (required)</span>}
      </label>
      {rendered}
      {hint && <p id={hintId} className="luma-field__hint">{hint}</p>}
      {error && <p id={errorId} className="luma-field__error">{error}</p>}
    </div>
  );
};

export default FormField;
